/**
 * LayeredDAGGraph.tsx - Layered Directed Acyclic Graph layout
 * 
 * CHANGE HISTORY:
 * - 251105_1850 danielbeer: Added interactive hover, click handlers, and dependency path highlighting
 * - 251031_2050 danielbeer: Initial creation
 * 
 * Layered layout organizing nodes by dependency levels.
 * Best for: Build order, compilation dependencies, task scheduling
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import type { GraphNode, GraphEdge, GraphOptions, GraphCallbacks, GraphStyle, DAGLayoutOptions } from './types';

export interface LayeredDAGGraphProps<T = any> extends GraphOptions, GraphCallbacks<T> {
  data: {
    nodes: GraphNode<T>[];
    edges: GraphEdge[];
  };
  style?: GraphStyle;
  dagOptions?: DAGLayoutOptions;
  enableZoom?: boolean;  // Enable/disable zoom (default: false)
  className?: string;
}

export const LayeredDAGGraph = <T = any,>({
  data,
  width = 1200,
  height = 800,
  style = {},
  dagOptions: dagOptionsProp = {},
  onNodeClick,
  onNodeHover,
  onEdgeClick,
  onEdgeHover,
  highlightConnected = true,
  enableZoom = false,  // DISABLED BY DEFAULT
  className = '',
}: LayeredDAGGraphProps<T>) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);

  useEffect(() => {
    if (!svgRef.current || data.nodes.length === 0) return;

    d3.select(svgRef.current).selectAll('*').remove();
    const svg = d3.select(svgRef.current);
    const dagOptions = { direction: 'LR' as const, layerSpacing: 200, nodeSpacing: 80, ...dagOptionsProp };

    // Calculate layers using topological sort
    const layers: string[][] = [];
    const nodeToLayer = new Map<string, number>();
    const inDegree = new Map<string, number>();
    const outEdges = new Map<string, string[]>();

    // Initialize
    data.nodes.forEach(node => {
      inDegree.set(node.id, 0);
      outEdges.set(node.id, []);
    });

    // Count in-degrees
    data.edges.forEach(edge => {
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
      const edges = outEdges.get(edge.source) || [];
      edges.push(edge.target);
      outEdges.set(edge.source, edges);
    });

    // Find nodes with no dependencies (layer 0)
    const queue: string[] = [];
    inDegree.forEach((degree, nodeId) => {
      if (degree === 0) queue.push(nodeId);
    });

    // Assign layers
    while (queue.length > 0) {
      const currentLayer: string[] = [];
      const layerSize = queue.length;

      for (let i = 0; i < layerSize; i++) {
        const nodeId = queue.shift()!;
        currentLayer.push(nodeId);
        nodeToLayer.set(nodeId, layers.length);

        // Process outgoing edges
        const targets = outEdges.get(nodeId) || [];
        targets.forEach(target => {
          const newDegree = (inDegree.get(target) || 0) - 1;
          inDegree.set(target, newDegree);
          if (newDegree === 0) queue.push(target);
        });
      }

      layers.push(currentLayer);
    }

    // Handle cycles by placing remaining nodes in final layer
    const processedNodes = new Set(Array.from(nodeToLayer.keys()));
    const remainingNodes = data.nodes.filter(n => !processedNodes.has(n.id));
    if (remainingNodes.length > 0) {
      layers.push(remainingNodes.map(n => n.id));
      remainingNodes.forEach(n => nodeToLayer.set(n.id, layers.length - 1));
    }

    // Calculate positions
    const margin = { top: 50, right: 50, bottom: 50, left: 50 };
    const isHorizontal = dagOptions.direction === 'LR' || dagOptions.direction === 'RL';
    const container = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const positions = new Map<string, { x: number; y: number }>();

    layers.forEach((layer, layerIndex) => {
      layer.forEach((nodeId, nodeIndex) => {
        if (isHorizontal) {
          const x = layerIndex * dagOptions.layerSpacing;
          const y = (nodeIndex - layer.length / 2) * dagOptions.nodeSpacing + (height - margin.top - margin.bottom) / 2;
          positions.set(nodeId, { x, y });
        } else {
          const x = (nodeIndex - layer.length / 2) * dagOptions.nodeSpacing + (width - margin.left - margin.right) / 2;
          const y = layerIndex * dagOptions.layerSpacing;
          positions.set(nodeId, { x, y });
        }
      });
    });

    // Optional zoom - only if enabled
    if (enableZoom) {
      const zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 4])
        .filter((event: any) => {
          const target = event.target;
          return target.tagName === 'svg';  // Only on background
        })
        .on('zoom', (event) => {
          container.attr('transform', `translate(${margin.left},${margin.top}) ${event.transform}`);
        });
      svg.call(zoom);
    }

    // Get connected nodes for highlighting (dependencies and dependents)
    const getConnectedNodes = (nodeId: string): { dependencies: Set<string>; dependents: Set<string> } => {
      const dependencies = new Set<string>();
      const dependents = new Set<string>();
      data.edges.forEach(edge => {
        if (edge.target === nodeId) dependencies.add(edge.source);
        if (edge.source === nodeId) dependents.add(edge.target);
      });
      return { dependencies, dependents };
    };

    const connected = hoveredNode ? getConnectedNodes(hoveredNode) : { dependencies: new Set<string>(), dependents: new Set<string>() };

    // Draw edges
    container.selectAll('.link')
      .data(data.edges)
      .join('line')
      .attr('class', 'link')
      .attr('x1', d => positions.get(d.source)?.x || 0)
      .attr('y1', d => positions.get(d.source)?.y || 0)
      .attr('x2', d => positions.get(d.target)?.x || 0)
      .attr('y2', d => positions.get(d.target)?.y || 0)
      .attr('stroke', d => {
        if (typeof style.edgeColor === 'function') return style.edgeColor(d);
        const edgeId = `${d.source}-${d.target}`;
        if (hoveredEdge === edgeId) return '#fbbf24';
        if (highlightConnected && hoveredNode && (d.source === hoveredNode || d.target === hoveredNode)) return '#60a5fa';
        return d.color || style.edgeColor || '#999';
      })
      .attr('stroke-width', d => {
        const edgeId = `${d.source}-${d.target}`;
        if (hoveredEdge === edgeId) return 4;
        if (highlightConnected && hoveredNode && (d.source === hoveredNode || d.target === hoveredNode)) return 3;
        return 2;
      })
      .attr('stroke-opacity', d => {
        const edgeId = `${d.source}-${d.target}`;
        if (hoveredEdge === edgeId) return 1;
        if (highlightConnected && hoveredNode && (d.source === hoveredNode || d.target === hoveredNode)) return 0.8;
        return 0.4;
      })
      .attr('marker-end', 'url(#arrowhead)')
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        const edgeId = `${d.source}-${d.target}`;
        setHoveredEdge(edgeId);
        if (onEdgeHover) onEdgeHover(d, event);
      })
      .on('mouseout', function(event) {
        setHoveredEdge(null);
        if (onEdgeHover) onEdgeHover(null, event);
      })
      .on('click', function(event, d) {
        if (onEdgeClick) onEdgeClick(d, event);
      });

    // Add arrow marker
    const defs = container.append('defs');
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', typeof style.edgeColor === 'string' ? style.edgeColor : '#999');

    // Draw nodes
    const nodes = container.selectAll('.node')
      .data(data.nodes)
      .join('g')
      .attr('class', 'node')
      .attr('transform', d => {
        const pos = positions.get(d.id);
        return `translate(${pos?.x || 0},${pos?.y || 0})`;
      });

    nodes.append('circle')
      .attr('r', d => 
        typeof style.nodeSize === 'function' ? style.nodeSize(d) :
        d.size || style.nodeSize || 20
      )
      .attr('fill', d => {
        if (typeof style.nodeColor === 'function') return style.nodeColor(d);
        if (d.id === hoveredNode) return '#fbbf24';
        if (highlightConnected && hoveredNode && connected.dependencies.has(d.id)) return '#ef4444'; // Dependencies in red
        if (highlightConnected && hoveredNode && connected.dependents.has(d.id)) return '#10b981'; // Dependents in green
        return d.color || style.nodeColor || '#3b82f6';
      })
      .attr('stroke', d => {
        if (d.id === hoveredNode) return '#f59e0b';
        if (highlightConnected && hoveredNode && (connected.dependencies.has(d.id) || connected.dependents.has(d.id))) return '#fff';
        return '#fff';
      })
      .attr('stroke-width', d => {
        if (d.id === hoveredNode) return 4;
        if (highlightConnected && hoveredNode && (connected.dependencies.has(d.id) || connected.dependents.has(d.id))) return 3;
        return 2;
      })
      .attr('opacity', d => {
        if (!hoveredNode) return 1;
        if (d.id === hoveredNode || connected.dependencies.has(d.id) || connected.dependents.has(d.id)) return 1;
        return 0.2;
      })
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        setHoveredNode(d.id);
        if (onNodeHover) onNodeHover(d, event);
      })
      .on('mouseout', function(event) {
        setHoveredNode(null);
        if (onNodeHover) onNodeHover(null, event);
      })
      .on('click', function(event, d) {
        if (onNodeClick) onNodeClick(d, event);
      });

    nodes.append('text')
      .text(d => d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', d => {
        const radius = typeof style.nodeSize === 'function' ? style.nodeSize(d) :
          d.size || style.nodeSize || 20;
        return radius + 15;
      })
      .attr('font-size', style.fontSize || '12px')
      .attr('font-family', style.fontFamily || 'sans-serif')
      .attr('fill', '#333');

    // Draw layer labels
    layers.forEach((layer, layerIndex) => {
      const pos = isHorizontal
        ? { x: layerIndex * dagOptions.layerSpacing, y: -20 }
        : { x: -20, y: layerIndex * dagOptions.layerSpacing };

      container.append('text')
        .attr('x', pos.x)
        .attr('y', pos.y)
        .attr('text-anchor', isHorizontal ? 'middle' : 'end')
        .attr('font-size', '11px')
        .attr('fill', '#999')
        .text(`Layer ${layerIndex}`);
    });

  }, [data, width, height, style, dagOptionsProp, hoveredNode, hoveredEdge, onNodeClick, onNodeHover, onEdgeClick, onEdgeHover, highlightConnected]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={`layered-dag-graph ${className}`}
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        background: style.backgroundColor || '#fafafa',
      }}
    />
  );
};

export default LayeredDAGGraph;
